/**
 * Invoices API client
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface CreateInvoiceRequest {
  debtor_id: string;
  invoice_number: string;
  amount_net: number;
  vat_percentage: number;
  invoice_date: string;
  due_date: string;
  description?: string;
  currency?: string;
}

export interface UpdateInvoiceRequest {
  description?: string;
  due_date?: string;
}

export interface InvoiceResponse {
  id: string;
  invoice_number: string;
  amount_net: number;
  amount_gross: number;
  vat_amount: number;
  vat_percentage: number;
  currency: string;
  coverage_percentage: number;
  coverage_amount: number;
  invoice_date: string;
  due_date: string;
  status: string;
  description: string;
  created_at: string;
  updated_at: string;
}

export interface InvoiceListResponse {
  invoices: InvoiceResponse[];
  total: number;
  limit: number;
  offset: number;
}

export async function createInvoice(
  token: string,
  request: CreateInvoiceRequest
): Promise<InvoiceResponse> {
  const response = await fetch(`${API_BASE_URL}/api/invoices`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create invoice');
  }

  return response.json();
}

export async function getInvoice(
  token: string,
  invoiceId: string
): Promise<InvoiceResponse> {
  const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch invoice');
  }

  return response.json();
}

export async function listInvoices(
  token: string,
  options?: {
    status?: string;
    skip?: number;
    limit?: number;
  }
): Promise<InvoiceListResponse> {
  const params = new URLSearchParams();
  if (options?.status) params.append('status', options.status);
  if (options?.skip) params.append('skip', options.skip.toString());
  if (options?.limit) params.append('limit', options.limit.toString());

  const url = new URL(`${API_BASE_URL}/api/invoices`);
  url.search = params.toString();

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to list invoices');
  }

  return response.json();
}

export async function updateInvoice(
  token: string,
  invoiceId: string,
  request: UpdateInvoiceRequest
): Promise<InvoiceResponse> {
  const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update invoice');
  }

  return response.json();
}

export async function updateInvoiceStatus(
  token: string,
  invoiceId: string,
  status: string
): Promise<InvoiceResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/invoices/${invoiceId}/status`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ status }),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update invoice status');
  }

  return response.json();
}

export async function deleteInvoice(
  token: string,
  invoiceId: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/invoices/${invoiceId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete invoice');
  }
}

export async function markInvoicePaid(
  token: string,
  invoiceId: string
): Promise<InvoiceResponse> {
  const response = await fetch(
    `${API_BASE_URL}/api/invoices/${invoiceId}/mark-paid`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to mark invoice as paid');
  }

  return response.json();
}
